import type { FederationRuntimePlugin } from "@module-federation/runtime";
import { HOST_NAME } from "src/module-federation-runtime/constants";
import { createSyntheticModuleFallback } from "./create-synthetic-module-fallback";

/**
 * Defines an errorLoadRemote hook for Module Federation.
 * Intent:
 * - Prevent hard crashes/white screens when a remote fails to resolve/load
 * whenever the error wouldn't be handled by our SDK components for rendering modules.
 * - Return a minimal synthetic module so React.lazy has a valid component.
 * - Log enough context to debug (remote id, lifecycle, origin, error).
 * Docs: https://module-federation.io/blog/error-load-remote.html
 */
const errorHandlingPlugin = (): FederationRuntimePlugin => {
    return {
        name: "error-handling-plugin",

        async errorLoadRemote(evt: {
            id: string;
            error: unknown;
            from: string;
            lifecycle: string;
            origin: { name?: string };
        }) {
            const { id = "unknown", error, from, lifecycle, origin } = evt;
            const message = error instanceof Error ? error.message : String(error);

            console.warn(
                `${HOST_NAME} module load error: Remote "${id}" failed during "${lifecycle}":` +
                    JSON.stringify(
                        {
                            lifecycle,
                            from,
                            origin: origin?.name ?? "unknown",
                            error: message,
                        },
                        null,
                        2
                    )
            );

            // Hand back a synthetic module; downstream SDK loaders will detect this and
            // swap in their richer fallback UI/telemetry.
            return createSyntheticModuleFallback({ id });
        },
    };
};

export default errorHandlingPlugin;
